import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Download, RefreshCcw, Filter, X } from "lucide-react";
import { api, API, downloadUrl, authQuery } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * GDPR audit log viewer. Shows WHO did WHAT on WHICH PII subject + when, IP,
 * and metadata. Filterable by actor, action, category, date range. Exports
 * the filtered set as CSV.
 *
 * Visibility:
 *   - obedience admin: full lodge log
 *   - child-lodge admin: scoped to their child lodge (server enforces)
 *   - officer: read-only (server enforces)
 *   - member: server returns nothing
 */
const ACTION_OPTIONS = [
  "MEMBER_VIEW",
  "MEMBER_CREATE",
  "MEMBER_UPDATE",
  "MEMBER_DELETE",
  "MEMBER_NFC_REGENERATE",
  "MEMBERS_EXPORT_PDF",
  "DUES_NOTICE_PDF",
  "DUES_BULK_PDF",
  "DUES_EXPORT_CSV",
  "ATTENDANCE_EXPORT_PDF",
  "ATTENDANCE_EXPORT_CSV",
  "JURISDICTION_OVERVIEW_PDF",
  "JURISDICTION_OVERVIEW_CSV",
  "MEMBER_DOCUMENT_DOWNLOAD",
  "TRANSFER_REQUEST",
  "TRANSFER_APPROVE",
  "TRANSFER_REJECT",
  "NFC_SCAN",
  "LOGIN_SUCCESS",
  "LOGIN_FAILED",
  "PASSWORD_CHANGED",
];

const CATEGORY_COLORS = {
  read: { bg: "#fdf4e0", fg: "#9a7530" },
  export: { bg: "#fde4d4", fg: "#a8552a" },
  write: { bg: "#e6f0e1", fg: "#2f6b3a" },
  auth: { bg: "#e7e2f5", fg: "#553c8b" },
  scan: { bg: "#dbe9f9", fg: "#1f4d7d" },
};

export default function AuditLogPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    action: "",
    category: "",
    actor_id: "",
    target_id: "",
    since: "",
    until: "",
  });

  const fetchRows = async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v) params.append(k, v);
      });
      params.append("limit", "300");
      const { data } = await api.get(`/audit-log?${params.toString()}`);
      setRows(data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const distinctActors = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (r.actor_id && !seen.has(r.actor_id)) {
        seen.set(r.actor_id, r.actor_name || r.actor_id);
      }
    });
    return Array.from(seen.entries()).map(([id, name]) => ({ id, name }));
  }, [rows]);

  const exportCsv = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params.append(k, v);
    });
    const aq = authQuery();
    const url = `${API}/audit-log/export.csv?${params.toString()}${params.toString() ? "&" : ""}${aq.slice(1)}`;
    downloadUrl(url, `audit-log-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  const clearFilters = () => {
    setFilters({ action: "", category: "", actor_id: "", target_id: "", since: "", until: "" });
    setTimeout(fetchRows, 0);
  };

  if (user?.role === "member") {
    return (
      <div className="gold-card" style={{ padding: "2rem", textAlign: "center" }}>
        <div className="kv-label" style={{ fontSize: "0.7rem", color: "#9a7530" }}>
          {t("auditLog.audit_log_is_not_available")}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="audit-log-page">
      <div className="page-header" style={{ marginBottom: "1.5rem" }}>
        <h1 className="page-title" style={{ fontFamily: "Cinzel, serif", fontSize: "1.6rem", letterSpacing: "0.18em" }}>
          {t("auditLog.audit_log")}
        </h1>
        <div className="page-subtitle" style={{ fontFamily: "Crimson Text, serif", color: "#858481", fontSize: "0.95rem" }}>
          {t("auditLog.every_pii_access_members_exports")}
        </div>
      </div>

      <div className="gold-card" style={{ padding: "1.2rem", marginBottom: "1.5rem" }}>
        <div className="kv-label" style={{ fontSize: "0.66rem", marginBottom: "0.6rem", color: "#9a7530", display: "flex", alignItems: "center", gap: 6 }}>
          <Filter size={12} /> {t("auditLog.filters")}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
          <select
            className="input-lodge"
            value={filters.action}
            onChange={(e) => setFilters({ ...filters, action: e.target.value })}
            data-testid="audit-filter-action"
          >
            <option value="">{t("auditLog.any_action")}</option>
            {ACTION_OPTIONS.map((a) => (
              <option key={a} value={a}>{a.replaceAll("_", " ").toLowerCase()}</option>
            ))}
          </select>
          <select
            className="input-lodge"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
            data-testid="audit-filter-category"
          >
            <option value="">{t("auditLog.any_category")}</option>
            <option value="read">read</option>
            <option value="export">export</option>
            <option value="write">write</option>
            <option value="auth">auth</option>
            <option value="scan">scan</option>
          </select>
          <select
            className="input-lodge"
            value={filters.actor_id}
            onChange={(e) => setFilters({ ...filters, actor_id: e.target.value })}
            data-testid="audit-filter-actor"
          >
            <option value="">{t("auditLog.any_user")}</option>
            {distinctActors.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <input
            type="date"
            className="input-lodge"
            value={filters.since}
            onChange={(e) => setFilters({ ...filters, since: e.target.value })}
            data-testid="audit-filter-since"
            placeholder={t("auditLog.since")}
          />
          <input
            type="date"
            className="input-lodge"
            value={filters.until}
            onChange={(e) => setFilters({ ...filters, until: e.target.value })}
            data-testid="audit-filter-until"
            placeholder={t("auditLog.until")}
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 10 }}>
          <button className="btn-outline-gold flex items-center gap-1" onClick={clearFilters} data-testid="audit-clear-filters">
            <X size={12} /> {t("auditLog.clear")}
          </button>
          <button className="btn-outline-gold flex items-center gap-1" onClick={fetchRows} disabled={loading} data-testid="audit-refresh">
            <RefreshCcw size={12} /> {t("auditLog.refresh")}
          </button>
          <button className="btn-primary-gold flex items-center gap-1" onClick={exportCsv} data-testid="audit-export-csv">
            <Download size={12} /> {t("auditLog.export_csv")}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner" data-testid="audit-error">{error}</div>
      )}

      <div className="gold-card" style={{ padding: 0, overflow: "auto" }}>
        <table style={{ width: "100%", minWidth: 720, borderCollapse: "collapse", fontFamily: "Crimson Text, serif" }} data-testid="audit-table">
          <thead style={{ background: "#0d1b2a", color: "#e8c97a" }}>
            <tr>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>{t("auditLog.when")}</th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>{t("auditLog.who")}</th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>{t("auditLog.action")}</th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>{t("auditLog.target")}</th>
              <th style={{ padding: "0.7rem 0.9rem", textAlign: "left", fontFamily: "Cinzel, serif", letterSpacing: "0.16em", fontSize: "0.7rem" }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "#9a7530", fontStyle: "italic" }}>{t("auditLog.loading")}</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "#9a7530", fontStyle: "italic" }} data-testid="audit-empty">{t("auditLog.no_audit_entries_match_the")}</td></tr>
            ) : (
              rows.map((r) => {
                const cat = CATEGORY_COLORS[r.category] || { bg: "#f5f0e8", fg: "#34322d" };
                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid #ead8a8" }} data-testid={`audit-row-${r.id}`}>
                    <td style={{ padding: "0.55rem 0.9rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem", color: "#34322d", whiteSpace: "nowrap" }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString(t("auditLog.en_gb")) : "—"}
                    </td>
                    <td style={{ padding: "0.55rem 0.9rem", fontSize: "0.88rem", color: "#0d1b2a" }}>
                      {r.actor_name || "—"}
                      {r.actor_role ? <span style={{ marginLeft: 6, fontSize: "0.7rem", color: "#9a7530" }}>· {r.actor_role}</span> : null}
                    </td>
                    <td style={{ padding: "0.55rem 0.9rem", fontSize: "0.78rem" }}>
                      <span
                        style={{
                          background: cat.bg, color: cat.fg, padding: "2px 8px",
                          fontFamily: "JetBrains Mono, monospace", fontSize: "0.7rem",
                          letterSpacing: "0.04em", textTransform: "lowercase",
                          marginRight: 6, borderRadius: 2,
                        }}
                      >
                        {r.category}
                      </span>
                      <span style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.78rem", color: "#34322d" }}>
                        {r.action.toLowerCase().replaceAll("_", " ")}
                      </span>
                    </td>
                    <td style={{ padding: "0.55rem 0.9rem", fontSize: "0.86rem", color: "#34322d" }}>
                      {r.target_label || r.target_id || "—"}
                      {r.metadata && Object.keys(r.metadata).length > 0 && (
                        <div style={{ fontSize: "0.7rem", color: "#9a7530", marginTop: 2 }}>
                          {Object.entries(r.metadata).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "0.55rem 0.9rem", fontFamily: "JetBrains Mono, monospace", fontSize: "0.74rem", color: "#9a7530" }}>
                      {r.ip || "—"}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "0.8rem", textAlign: "center", color: "#9a7530", fontSize: "0.75rem", fontStyle: "italic" }}>
        {rows.length} {t("auditLog.entries")} · {t("auditLog.auto_deleted_after_365_days")}
      </div>
    </div>
  );
}
